import { DOCUMENT_STATES, RequestCreateLogAsCredentials, RequestCreateLoginCredentials, RequestCreateLogout, RequestCreatePasswordReset, RequestReadMoneyboxes, RequestReadUserMe, RequestUpdateUserMe } from '@yescapa-dev/ysc-api-js/modern'
import type { APIPasswordResetPayload, AppLogAsPayload, AppLogInPayload, AppMoneyboxes, AppUserMe, AppUserUpdatePayload } from '@yescapa-dev/ysc-api-js/modern'
import { useFeatureFlag } from '~/composables/useFeatureFlag'
import { AUTH_LOGIN_EVENT, AUTH_LOGIN_METHOD_PARAM } from '~/utils/analytics/auth'

export const useUserStore = defineStore('user', () => {
  const showSSOModal = ref(false)
  const returnUrl = ref<string | null>(null)

  const openSSOModal = (to: string | null) => {
    if (useFeatureFlag('ssoLogin')) {
      showSSOModal.value = true
      if (to) returnUrl.value = to
    }
  }

  const closeSSOModal = () => {
    if (useFeatureFlag('ssoLogin')) {
      const route = useRoute()
      const { 'sso-modal': _, ...queryWithoutSSO } = route.query

      navigateTo({ query: queryWithoutSSO }, { replace: true })

      showSSOModal.value = false
      returnUrl.value = null
    }
  }

  const loggedIn = ref(false)

  const { event } = useGtag()
  const user = ref<AppUserMe | null>(null)

  const isLoggedIn = computed(() => user.value !== null)
  const isStaff = computed(() => user.value?.is_staff ?? false)
  const isOwner = computed(() => user.value?.is_owner ?? false)

  const moneyboxes = ref<AppMoneyboxes>([])

  const driverIdentityChecked = ref(false)
  const isEmergencyWarningSeen = ref(false)

  const {
    public: {
      api: {
        rental: { key: client_id },
      },
    },
  } = useRuntimeConfig()

  const { writeTokens, readAccessToken } = useYscTokens()

  const logIn = async (payload: MaybeRefOrGetter<Omit<AppLogInPayload, 'client_id'>>) => {
    event(AUTH_LOGIN_EVENT, { [AUTH_LOGIN_METHOD_PARAM]: 'email' })

    const tokens = await useNuxtApp().$apiConsumer.send(new RequestCreateLoginCredentials({
      payload: {
        ...toValue(payload),
        client_id,
      },
    }))

    writeTokens(tokens)
    await fetchUser()
  }

  const logAs = async (payload: AppLogAsPayload) => {
    const tokens = await useNuxtApp().$apiConsumer.send(new RequestCreateLogAsCredentials({
      payload,
    }))

    writeTokens(tokens)
    await fetchUser()
  }

  const logOut = async () => {
    const token = readAccessToken()
    if (!isDefined(token)) {
      // No access token, user already logged out
      return
    }

    await useNuxtApp().$apiConsumer.send(new RequestCreateLogout({
      payload: {
        token,
        client_id,
      },
    }))
    await $fetch('/api/internals/logout', { method: 'GET' })
    reloadNuxtApp({ path: '/' })
  }

  const fetchUser = async ({ refresh = false } = {}) => {
    const { setSentryUser } = useSetSentryUser()
    if (!refresh && user.value !== null) {
      return
    }

    user.value = await useNuxtApp().$apiConsumer.send(new RequestReadUserMe())
    setSentryUser()
  }

  const updateUser = async (payload: AppUserUpdatePayload) => {
    user.value = await useNuxtApp().$apiConsumer.send(new RequestUpdateUserMe({ payload }))
  }

  const updatePassword = async (payload: APIPasswordResetPayload) => {
    await useNuxtApp().$apiConsumer.send(new RequestCreatePasswordReset({
      payload,
    }))
  }

  const fetchMoneyboxes = async () => {
    moneyboxes.value = await useNuxtApp().$apiConsumer.send(new RequestReadMoneyboxes())
  }

  const areMandatoryGuestDocumentsValidated = computed(() => {
    if (!isLoggedIn.value || !user.value?.documents) {
      return false
    }
    return (
      useDocumentsMergedStatus({
        // FIXME: remove `&& document_type !== 'id_proof'` when api has settled the retro compatibility problems app
        documents: user.value.documents.filter(({ is_mandatory, document_type }) => is_mandatory && document_type !== 'id_proof'),
      }) === DOCUMENT_STATES.VALIDATED
    )
  })

  const areMandatoryGuestDocumentsInValidation = computed(() => {
    if (!isLoggedIn.value || !user.value?.documents) {
      return false
    }
    return (
      useDocumentsMergedStatus({
        documents: user.value.documents.filter(({ is_mandatory }) => is_mandatory),
      }) === DOCUMENT_STATES.VALIDATION_ASKED
    )
  })

  return {
    showSSOModal: readonly(showSSOModal),
    returnUrl: readonly(returnUrl),
    openSSOModal,
    closeSSOModal,
    loggedIn,
    user,
    isLoggedIn,
    isStaff,
    isOwner,
    moneyboxes,
    driverIdentityChecked,
    isEmergencyWarningSeen,
    logIn,
    logAs,
    logOut,
    fetchUser,
    updateUser,
    updatePassword,
    fetchMoneyboxes,
    areMandatoryGuestDocumentsValidated,
    areMandatoryGuestDocumentsInValidation,
  }
})
